/**
 * Pure functions for evaluating "are we currently in quiet hours?" and "when
 * does the next active-hours window start?" in the contact's timezone.
 *
 * Mirrors `server/services/organization/quiet-hours-service.js`; the server
 * remains authoritative for actual message scheduling. These helpers exist so
 * the in-chat banner and modal can derive UI state without an HTTP roundtrip.
 *
 * Timezone primitives (`getDateParts`, `partsToUtcMs`, …) and the
 * contact-zone / org-config resolvers live in the framework-neutral
 * `timezone.ts` / `organization-config.ts` modules so non-QH features (e.g.
 * scheduled messages) can share them without importing this module.
 */

import { QUIET_HOURS } from '@app/constants/feature-definitions';
import { formatApproximateDateTime } from '@app/i18n/formatters/date';
import type { ReadableQueryClient } from '@app/query-client';
import { isFeatureEnabledFromCache } from '@app/services/organization-features';
import { DEFAULT_ACTIVE_HOURS } from '@app/types/api/organization';
import type {
  ActiveHoursConfig,
  DaySchedule,
  QuietHoursConfig,
  QuietHoursSettings,
} from '@app/types/api/organization';
import type { StateType } from '@app/types/reducer-state';

import {
  resolveOrganizationActiveHours,
  resolveOrganizationQuietHours,
  resolveOrganizationQuietHoursSettings,
} from './organization-config';
import { getDateParts, type ZonedDateParts } from './timezone';

const CLEARING_WINDOW = 10 * 60 * 1000;

const minutesOfDay = (parts: ZonedDateParts): number =>
  parts.hour * 60 + parts.minute;

/**
 * True when `now` falls outside the configured active-hours window. Signature
 * differs from the server's (which takes a job and resolves tz async): on the
 * FE we already have config + tz + clock, so we keep this pure and memoizable
 * for the reselect chain.
 */
export const isCurrentlyQuietHours = (
  quietHours: QuietHoursConfig | undefined,
  timeZone: string | null,
  now: Date,
): boolean => {
  if (!quietHours?.enabled || !timeZone) return false;
  const parts = getDateParts(now, timeZone);
  const day: DaySchedule | undefined = quietHours.schedule?.[parts.dayOfWeek];
  if (!day) return false;
  const mins = minutesOfDay(parts);
  // Minute-granularity only — use >= on end so the final minute of active
  // hours (e.g. 8:00:01–8:00:59) matches the server, which compares full
  // timestamps against endTime with seconds zeroed (`current > endTimeZoned`).
  return !day.enabled || mins < day.startTimeMins || mins >= day.endTimeMins;
};

/**
 * Determines if it is quiet hours for the entire calendar day associated with a date.
 * Needs the applicable timezone to determine the calendar day.
 */
export const isEntireDayQuietHours = (
  quietHours: QuietHoursConfig | undefined,
  timeZone: string | null,
  date: Date,
): boolean => {
  if (!quietHours?.enabled || !timeZone) return false;
  const parts = getDateParts(date, timeZone);
  const day: DaySchedule | undefined = quietHours.schedule?.[parts.dayOfWeek];
  if (!day) return false;
  return !day.enabled || day.startTimeMins >= day.endTimeMins;
};

/**
 * Approximate send date and time for a quiet hours message
 * (Jan 15, 2026, ~2:30 PM). Rendered in the contact's timezone when `timeZone`
 * is given, falling back to the operator's local zone.
 */
export const formatQuietHoursSendTime = (
  sendTimeMs: number | string | Date | null | undefined,
  timeZone?: string | null,
): string => {
  if (sendTimeMs === null || sendTimeMs === undefined || sendTimeMs === '') {
    return '';
  }
  const sendDate =
    sendTimeMs instanceof Date ? sendTimeMs : new Date(sendTimeMs);
  if (Number.isNaN(sendDate.getTime())) return '';

  const windowStart = new Date(
    Math.floor(sendDate.getTime() / CLEARING_WINDOW) * CLEARING_WINDOW,
  );
  return formatApproximateDateTime(windowStart, timeZone ?? undefined);
};

// Mirrors the logic in admin-config.ts. Duplicated here to keep the quiet-hours
// logic layer free of an admin-config import cycle.
export const isCentralizedActiveHoursFeatureFlagOn = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_CENTRALIZED_ACTIVE_HOURS as unknown as boolean | string;
  return flagValue === true || flagValue === 'true';
};

export const isQuietHoursUseActiveHoursFlagOn = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_QUIET_HOURS_USE_ACTIVE_HOURS as unknown as boolean | string;
  return flagValue === true || flagValue === 'true';
};

export const isQuietHoursUsingActiveHours = (): boolean =>
  isQuietHoursUseActiveHoursFlagOn() && isCentralizedActiveHoursFeatureFlagOn();

/**
 * Resolves the effective quiet hours config the same way for every consumer.
 * Mirrors the server's `resolveOrgQuietHoursConfig`: when FEATURE_FLAG_CENTRALIZED_ACTIVE_HOURS
 * is on, quiet hours is driven by the org's Active Hours schedule and the public
 * QUIET_HOURS feature toggle. Otherwise it uses the legacy org.quietHours config.
 * Returns a QuietHoursConfig-shaped object so downstream `isCurrentlyQuietHours`
 * stays source-agnostic.
 */
export const resolveEffectiveQuietHoursConfig = ({
  organizationId,
  queryClient,
  reduxQuietHours,
  reduxActiveHours,
  useActiveHours,
  quietHoursFeatureEnabled,
}: {
  organizationId?: string | null;
  queryClient?: ReadableQueryClient | null;
  reduxQuietHours?: QuietHoursConfig;
  reduxActiveHours?: ActiveHoursConfig;
  useActiveHours: boolean;
  quietHoursFeatureEnabled: boolean;
}): QuietHoursConfig | undefined => {
  if (useActiveHours) {
    const activeHours =
      resolveOrganizationActiveHours({
        organizationId,
        queryClient,
        reduxActiveHours,
      }) ?? DEFAULT_ACTIVE_HOURS;
    return {
      enabled: quietHoursFeatureEnabled,
      timeZone: activeHours.timeZone,
      schedule: activeHours.schedule,
    };
  }
  return resolveOrganizationQuietHours({
    organizationId,
    queryClient,
    reduxQuietHours,
  });
};

/**
 * Resolves the active hours override schedule for a contact, or null
 * when it doesn't apply.
 */
export const resolveContactActiveHoursOverride = (
  quietHoursSettings: QuietHoursSettings | undefined,
  contactTimeZone: string | null,
): DaySchedule[] | null => {
  if (!contactTimeZone) return null;
  const contactActiveHours = quietHoursSettings?.contactActiveHours;
  return contactActiveHours?.enabled && contactActiveHours.schedule?.length
    ? contactActiveHours.schedule
    : null;
};

/**
 * Resolved quiet-hours evaluation context: the schedule config (with any
 * contact override applied) and the timezone to evaluate it in.
 */
export interface QuietHoursContext {
  config: QuietHoursConfig | undefined;
  evaluationTimeZone: string | null;
}

/**
 * Determines the quiet-hours evaluation context: Factors in org settings,
 * contact timezone, and schedule overrides.
 *
 * Unlike its server-side counterpart (also called resolveQuietHoursContext),
 * this takes the global config, override settings, and contact timezone as
 * already-resolved inputs rather than resolving them itself, since the
 * data source differs across contexts. Invoked by useQuietHoursContext (hook),
 * resolveQuietHoursContextFromStore (thunk), and computeQuietHoursState
 * (memoized reselect selector) — each resolves the inputs its own way, then
 * uses this override/timezone resolution.
 */
export const resolveQuietHoursContext = ({
  globalConfig,
  contactTimeZone,
  quietHoursSettings,
}: {
  globalConfig: QuietHoursConfig | undefined;
  contactTimeZone: string | null;
  quietHoursSettings?: QuietHoursSettings;
}): QuietHoursContext => {
  const overrideSchedule = resolveContactActiveHoursOverride(
    quietHoursSettings,
    contactTimeZone,
  );
  // Only apply the per-contact override when quiet hours is enabled.
  // Other features that use active hours may wish to use the override
  // schedule too, but only if an org has actually enabled it via QH.
  const config =
    globalConfig?.enabled && overrideSchedule
      ? { ...globalConfig, schedule: overrideSchedule }
      : globalConfig;
  return {
    config,
    evaluationTimeZone: contactTimeZone || config?.timeZone || null,
  };
};

/**
 * Imperative (non-hook) twin of `useQuietHoursContext` for Redux thunks.
 */
export const resolveQuietHoursContextFromStore = ({
  state,
  queryClient,
  contactTimeZone,
}: {
  state: StateType;
  queryClient?: ReadableQueryClient | null;
  contactTimeZone: string | null;
}): QuietHoursContext => {
  const organizationId = state.organizations?._id;
  const useActiveHours = isQuietHoursUsingActiveHours();

  const globalConfig = resolveEffectiveQuietHoursConfig({
    organizationId,
    queryClient,
    reduxQuietHours: state.organizations?.quietHours,
    reduxActiveHours: state.organizations?.activeHours,
    useActiveHours,
    quietHoursFeatureEnabled: isFeatureEnabledFromCache(
      queryClient,
      QUIET_HOURS,
      { failClosed: true },
    ),
  });

  // Quiet hours settings only apply when using the active hours model, not
  // the legacy organization.quietHours config.
  const quietHoursSettings = useActiveHours
    ? resolveOrganizationQuietHoursSettings({
        organizationId,
        queryClient,
        reduxQuietHoursSettings:
          state.organizations?.organizationSettings?.quietHours,
      })
    : undefined;

  return resolveQuietHoursContext({
    globalConfig,
    contactTimeZone,
    quietHoursSettings,
  });
};
